import { MERCADOS, PORTES, SETORES, TIPOS_LOCAL, TURNOS, UFS, type CompanyProfile, type Local } from './companyProfile'

/**
 * s67 / PE2 (Marcelo 25/10/2026 11:55, "APROVADO PE2 ; F1-F4 sim"; G3 A) -- PESQUISA ASSISTIDA do perfil, SEM IA no sistema.
 *
 * 1. "Gerar texto de pesquisa": um texto PRONTO para o consultor colar numa ferramenta de pesquisa FORA do sistema. O texto
 *    NAO leva o nome nem o CNPJ da empresa (G3): o consultor escreve isso la, no lugar marcado. Feito aqui, na tela: nenhuma
 *    chamada a API, nada sai do sistema.
 * 2. "Colar resultado": a resposta volta no formato FIXO "Campo: valor | fonte: link", uma linha por campo (e uma por
 *    local). A leitura e por regra (sem IA): o que casa vira RASCUNHO no formulario -- nada e gravado ate "Salvar perfil";
 *    o que nao casa aparece na lista "nao entendi", para o consultor ver.
 */

export const NOME_AQUI = '[ESCREVA AQUI O NOME E O CNPJ DA EMPRESA]'

const rotulos = (xs: [string, string][]) => xs.map(([, r]) => r).join('; ')

export function gerarTextoPesquisa(): string {
  return [
    `Pesquise na internet a empresa ${NOME_AQUI} e responda SOMENTE no formato abaixo, uma linha por campo, sem texto ` +
      'antes ou depois. Em cada linha, depois de "| fonte:", ponha o link de onde tirou a informação. Se não achar, não ' +
      'escreva a linha. Não invente. Não escreva nomes de pessoas.',
    '',
    `Setor: <um destes: ${rotulos(SETORES)}> | fonte: <link>`,
    'Ramo: <até 80 letras, ex.: provedor de internet> | fonte: <link>',
    'Cidade: <cidade da sede> | fonte: <link>',
    'UF: <sigla do estado da sede, ex.: SP> | fonte: <link>',
    `Porte: <um destes: ${rotulos(PORTES)}> | fonte: <link>`,
    'Atividades: <o que a empresa faz, até 200 letras> | fonte: <link>',
    'Produtos: <até 10, separados por ;> | fonte: <link>',
    'Processos: <até 10 processos principais, separados por ;> | fonte: <link>',
    `Turnos: <um ou mais destes, separados por ;: ${rotulos(TURNOS)}> | fonte: <link>`,
    'Certificações: <até 10, separadas por ;> | fonte: <link>',
    `Mercado: <um ou mais destes, separados por ;: ${rotulos(MERCADOS)}> | fonte: <link>`,
    `Local: <nome do local>; <cidade>; <UF>; <tipo: ${rotulos(TIPOS_LOCAL)}> | fonte: <link>`,
    '(repita a linha "Local:" para cada local: fábrica, escritório, loja, centro de distribuição, obra)',
  ].join('\n')
}

const sem = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()

/** Valor de lista fechada: aceita o rotulo ("Indústria", "escala 12x36") ou o codigo ("industria"), sem acento. */
function fechado(valor: string, opcoes: [string, string][]): string | null {
  const v = sem(valor)
  const achou = opcoes.find(([c, r]) => sem(c) === v || sem(r) === v)
  return achou ? achou[0] : null
}

const CAMPOS: Record<string, keyof CompanyProfile> = {
  setor: 'setor', ramo: 'ramo', cidade: 'cidade', uf: 'uf', porte: 'porte', atividades: 'atividades',
  produtos: 'produtos', 'produtos e servicos': 'produtos', processos: 'processos', 'processos-chave': 'processos',
  turnos: 'turnos', certificacoes: 'certificacoes', mercado: 'mercado', local: 'locais', locais: 'locais',
}

export interface ResultadoLido {
  rascunho: Partial<CompanyProfile>
  fontes: Record<string, string>
  lidos: number
  naoEntendidas: string[]
}

const link = (s: string) => (/^https?:\/\/\S+$/i.test(s.trim()) ? s.trim() : null)
const itens = (s: string, max: number) =>
  [...new Set(s.split(';').map((x) => x.replace(/\s+/g, ' ').trim()).filter(Boolean))].slice(0, max)

export function lerResultado(texto: string): ResultadoLido {
  const rascunho: Partial<CompanyProfile> = {}
  const fontes: Record<string, string> = {}
  const naoEntendidas: string[] = []
  const locais: Local[] = []
  let lidos = 0
  for (const bruta of texto.split(/\r?\n/)) {
    const linha = bruta.trim()
    if (!linha) continue
    const m = /^([^:|]+):\s*(.*?)\s*(?:\|\s*fonte:\s*(.*))?$/i.exec(linha)
    const campo = m ? CAMPOS[sem(m[1])] : undefined
    const valor = m ? m[2].trim() : ''
    if (!m || !campo || !valor || valor.startsWith('<')) {
      naoEntendidas.push(linha)
      continue
    }
    let ok = true
    switch (campo) {
      case 'setor':
      case 'porte': {
        const c = fechado(valor, campo === 'setor' ? SETORES : PORTES)
        if (c) rascunho[campo] = c
        else ok = false
        break
      }
      case 'uf': {
        const u = valor.toUpperCase()
        if (UFS.includes(u)) rascunho.uf = u
        else ok = false
        break
      }
      case 'ramo':
      case 'cidade':
        rascunho[campo] = valor.slice(0, 80)
        break
      case 'atividades':
        rascunho.atividades = valor.slice(0, 200)
        break
      case 'produtos':
      case 'processos':
        rascunho[campo] = itens(valor, 10).map((x) => x.slice(0, 80))
        break
      case 'certificacoes':
        rascunho.certificacoes = itens(valor, 10).map((x) => x.slice(0, 60))
        break
      case 'turnos':
      case 'mercado': {
        const cs = itens(valor, 10).map((x) => fechado(x, campo === 'turnos' ? TURNOS : MERCADOS))
        if (cs.length > 0 && cs.every((x) => x !== null)) rascunho[campo] = [...new Set(cs as string[])]
        else ok = false
        break
      }
      case 'locais': {
        const [nome, cidade, uf, tipo] = valor.split(';').map((x) => x.trim())
        const t = fechado(tipo ?? '', TIPOS_LOCAL)
        const u = (uf ?? '').toUpperCase()
        if (nome && t && (!u || UFS.includes(u)) && locais.length < 30) {
          if (!locais.some((x) => sem(x.nome) === sem(nome)))
            locais.push({ nome: nome.slice(0, 60), cidade: cidade ? cidade.slice(0, 80) : null, uf: u || null, tipo: t })
        } else ok = false
        break
      }
    }
    if (!ok) {
      naoEntendidas.push(linha)
      continue
    }
    lidos++
    const f = m[3] ? link(m[3]) : null
    if (f) fontes[campo] = f
  }
  if (locais.length > 0) rascunho.locais = locais
  return { rascunho, fontes, lidos, naoEntendidas }
}
